import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../../../common/services/common.service';
import { ComponentMessagingService } from '../../../../common/services/component-messaging.service';
import { HttpService } from '../../../../common/services/http.service';
import { ApiMethod, PostURL } from '../../../../common/const/requests-methods';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { InfoConfirmationComponent } from 'src/app/common/components/ui-grid/modals/info-confirmation/info-confirmation.component';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from './user.service';
import { Store } from '@ngxs/store';
import { ClearAdvanceSearch } from 'src/app/state/advance-search/advance-search.actions';
import { ClearAuthState } from 'src/app/state/auth/auth.actions';
import { ClearUpdateData } from 'src/app/state/update-settings/update-settings.actions';

@Injectable({
    providedIn: 'root'
})

export class AuthService {

    constructor(
        private router: Router,
        private httpService: HttpService,
        private userService: UserService,
        private commonService: CommonService,
        private modalController: ModalController,
        private componentMessagingService: ComponentMessagingService,
        private translateService: TranslateService,
        private store: Store) { }

    login(email: string, password: string, rememberMe: boolean = false): Observable<any> {
        return this.httpService.requestCall(environment.apiUrl + PostURL.LOGIN, ApiMethod.POST, {}, { email, password, rememberMe });
    }

    register(user: any): Observable<any> {
        return this.httpService.requestCall(environment.apiUrl + PostURL.REGISTER, ApiMethod.POST, {}, user);
    }

    getCsrfToken(path: string): Observable<any> {
        return this.httpService.requestCall(environment.apiUrl + path, ApiMethod.GET);
    }

    getCsrfTokenAsPromise(path: string) {
        return this.getCsrfToken(path).toPromise();
    }

    checkRecaptha(response: any) {
        return this.httpService.requestCall(environment.apiUrl + PostURL.VALIDATE_CAPTCHA, ApiMethod.POST, {}, { response }).toPromise();
    }

    async tryLogin(email: string, password: string, rememberMe: boolean = false) {
        try {
            const result = await this.login(email, password, rememberMe).toPromise();

            if (result?.id && result?.email && result?.tokenExpires) {
                await this.userService.setUser({
                    id: result?.id,
                    email: result?.email,
                    name: result?.name,
                    roles: result?.roles,
                    lastLoginAt: result?.lastLoginAt
                }).toPromise();

                await this.userService.setSettings({
                   pagination: result?.settings?.itemPerPageList,
                   includeTagAliasesDuringTagQuickLookup: result?.settings?.includeTagAliasesDuringTagQuickLookup,
                   lookupRelatedTags: result?.settings?.lookupRelatedTags
                }).toPromise();

                await this.userService.setAuthToken({
                    expires: result.tokenExpires
                }).toPromise();

                this.componentMessagingService.sendMessage('login', { success: true });

                localStorage.setItem('locationChanged', result?.locationChanged);

                return { success: true, lastLoginAt: result?.lastLoginAt };
            }
        } catch (error) {
            throw error;
        }

        return { success: false };
    }

    isAuthTokenValid() {
        const user = this.userService.getUser();
        const authToken = this.userService.getAuthToken();

        if (!user || !authToken) {
            return false;
        }

        if (authToken?.expires >= Math.floor(Date.now() / 1000) && user.roles.includes('ROLE_USER') && !user.roles.includes('ROLE_ADMIN')) {
            return true;
        }

        return false;
    }

    logout() {
        return this.httpService.requestCall(
            environment.apiUrl + (this.userService.getUser()?.roles?.includes('ROLE_ADMIN') ? PostURL.LOGOUT_ADMIN : PostURL.LOGOUT),
            ApiMethod.POST
        );
    }

    logoutUserDueToInactivity() {
        this.logout().subscribe(() => {
          this.store.dispatch([new ClearAdvanceSearch(), new ClearAuthState(), new ClearUpdateData()]).subscribe(() => {
                this.componentMessagingService.sendMessage('logout');

                this.router.navigate(['/']).then(async () => {
                    const modal = await this.modalController.create({
                        component: InfoConfirmationComponent,
                        componentProps: {
                            inputMessage:
                                '<div class="idle-disconnect-message">' +
                                this.translateService.instant('IDLE.KICK_MESSAGE') +
                                '</div>',
                        },
                        cssClass: ['info-modal-class', 'idle-disconnect-modal']
                    });

                    await modal.present();
                });
            });
        });
    }
}
